import { useEffect, useId, useRef } from 'react'
import { IDataPoint, TDataRef } from '../data.types.ts'
import eventBus from '../../eventBus'
import dataSubscriptionManager from '../data.subscriptionManager.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import useHttpLoader from '../../../shared/hooks/useHttpLoader.ts'
import objectUtils from '@/utils/object.utils.ts'
import dataApi from '@/services/data/data.api.ts'
import { useRecoilValue } from 'recoil'
import parametersSelectors from '@/pages/Parameters/parameters.selectors.ts'
import dataUtils from '@/services/data/data.utils.ts'
import equipmentSelectors from '@/pages/Equipment/equipment.selectors.ts'
import { TDeviceProtocol } from '@/services/ws/ws.types.ts'

export interface IUseDataRefProps {
  // За указанный временной промежуток будут запрошенны данные
  initialTsFrom: number
  initialTsTo: number

  devices: Array<IEquipment['id']>
  parameters: Array<IParameter['id']>
  live: boolean
  aggregate?: boolean
  onNewValueReceived?: () => void
}

const useDataRef = (props: IUseDataRefProps) => {
  const { loading, wait } = useHttpLoader()
  const data = useRef<TDataRef>({})
  const lastValues = useRef<Record<IEquipment['id'], Record<IParameter['id'], { ts: number; value: number }>>>({})

  const subscriptionId = useId()

  const parametersSourceIdToIdMap = useRecoilValue(parametersSelectors.getSourceIdToIdMap)
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const equipmentMap = useRecoilValue(equipmentSelectors.getEquipmentMap)

  const firstRender = useRef(true)
  const prevConfig = useRef<IUseDataRefProps>(props)

  useEffect(() => {
    if (!props.live) return

    const minTsDelta = 0

    const parameters = [...props.parameters]

    dataSubscriptionManager.addSubscription({
      devices: props.devices,
      deviceProtocols: props.devices.map((deviceId) => equipmentMap[deviceId]?.protocolId as TDeviceProtocol),
      parameters: parameters,
      parametersSourceIds: props.parameters.map((p) => parametersMap[p]?.sourceId),
      id: subscriptionId,
    })

    const ebUnsub = eventBus.sub('data.point', (point: IDataPoint) => {
      if (!data || !data.current) return
      if (!props.devices.includes(point.equipmentId)) return
      if (!point.values.some((v) => parameters.includes(v.parameterId))) return

      const normalizedPoint: IDataPoint = dataUtils.fillDataPointWithRequiredLastValues({
        point,
        lastValuesRef: lastValues,
        equipmentId: point.equipmentId,
        parameters: parameters,
      })

      for (const value of point.values) {
        if (!lastValues.current[point.equipmentId]) lastValues.current[point.equipmentId] = {}

        lastValues.current[point.equipmentId][value.parameterId] = { value: value.value || 0, ts: point.ts }
      }

      if (Array.isArray(data.current[point.equipmentId])) {
        const prevPoint = data.current[point.equipmentId][data.current[point.equipmentId].length - 1]
        if (prevPoint && Math.abs(point.ts - prevPoint.ts) < minTsDelta) return

        data.current[point.equipmentId].push(normalizedPoint)
      } else {
        data.current[point.equipmentId] = [normalizedPoint]
      }

      if (props.onNewValueReceived) {
        props.onNewValueReceived()
      }
    })

    return () => {
      dataSubscriptionManager.removeSubscription(subscriptionId)
      ebUnsub()
    }
  }, [props.devices, props.parameters, props.live])

  useEffect(() => {
    if (!props.devices.length || !props.initialTsFrom || !props.initialTsTo) return

    if (firstRender.current) {
      firstRender.current = false
    } else {
      if (objectUtils.isJsonEqual(props, prevConfig.current)) {
        return
      } else {
        prevConfig.current = objectUtils.deepCopy(props)
      }
    }

    const parameters = props.parameters.map((p) => parametersMap[p]?.sourceId).filter(Boolean)

    const request = dataApi.getAggregated({
      protocolId: equipmentMap[props.devices[0]].protocolId,
      toTime: props.initialTsTo,
      fromTime: props.initialTsFrom,
      deviceId: props.devices[0],
      parameters,
      aggregationFactor: props.aggregate === false ? undefined : 100,
    })

    wait(request, (resp) => {
      if (resp.status === 'success') {
        data.current[resp.body.device_id] = resp.body.timestamps.map((t) => ({
          ts: Math.round(t.ts / 1000 / 1000),
          equipmentId: resp.body.device_id,
          values: t.values.map((v) => ({ parameterId: parametersSourceIdToIdMap[v.name], value: v.value })),
        }))

        const lastPoint = data.current[resp.body.device_id][data.current[resp.body.device_id].length - 1]
        const now = Date.now()

        if (lastPoint) {
          lastValues.current[resp.body.device_id] = lastPoint.values.reduce(
            (acc, val) => ({ ...acc, [val.parameterId]: { value: val.value || 0, ts: now } }),
            {}
          )
        }
      }

      if (props.onNewValueReceived) {
        props.onNewValueReceived()
      }
    })
  }, [
    props.initialTsFrom,
    props.initialTsTo,
    props.live,
    props.aggregate,
    props.devices,
    props.parameters,
    props.onNewValueReceived,
  ])

  useEffect(() => {
    let interval
    if (props.live) {
      interval = setInterval(() => {
        dataUtils.clearUsedPoints(data, props.initialTsFrom, props.initialTsTo)
      }, 1000)
    }

    return () => {
      clearInterval(interval)
    }
  }, [props.initialTsFrom, props.initialTsTo, props.live])

  return { dataRef: data, loading }
}

export default useDataRef
